'use client'

import { Suspense, useState } from 'react'
import { signIn } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import Image from 'next/image'
import { Loader2 } from 'lucide-react'

function LoginForm() {
  const router = useRouter()
  const params = useSearchParams()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const result = await signIn('credentials', { username, password, redirect: false })
      if (result?.error) {
        setError('Invalid username or password.')
        setLoading(false)
        return
      }
      // Deliberately stays busy: the slow part — the server rendering the app —
      // only starts here, and this form unmounts once that navigation lands.
      router.replace(params.get('callbackUrl') ?? '/')
    } catch {
      setError('Sign-in failed. Please try again.')
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} aria-busy={loading} className="flex flex-col gap-4">
      {loading && (
        // Pinned to the card's top edge (the card is `relative overflow-hidden`)
        <div
          role="progressbar"
          aria-label="Signing in"
          className="absolute inset-x-0 top-0 h-1 overflow-hidden bg-primary/15"
        >
          <div className="h-full w-2/5 bg-primary motion-safe:animate-[indeterminate-sweep_1.1s_ease-in-out_infinite] motion-reduce:w-full motion-reduce:opacity-60" />
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-semibold text-foreground" htmlFor="username">
          Username
        </label>
        <input
          id="username"
          type="text"
          autoComplete="username"
          required
          disabled={loading}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-semibold text-foreground" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          disabled={loading}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
        />
      </div>

      {error && <p className="text-sm text-destructive font-semibold text-center">{error}</p>}

      <button
        type="submit"
        disabled={loading}
        aria-busy={loading}
        className="bg-primary text-white rounded-md px-4 py-2 text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-80 disabled:cursor-wait mt-1 inline-flex items-center justify-center gap-2"
      >
        {loading && (
          <Loader2 data-testid="login-spinner" size={16} aria-hidden="true" className="motion-safe:animate-spin" />
        )}
        {loading ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  )
}

export default function LoginPage() {
  return (
    <div className="w-full max-w-sm">
      <div className="relative overflow-hidden bg-background border rounded-2xl shadow-lg p-8 flex flex-col gap-6">
        <div className="flex flex-col items-center gap-3">
          <Image src="/techmo_icon_192.png" alt="Techmo" width={48} height={48} />
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground">Quality TTS</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Sign in to continue</p>
          </div>
        </div>
        <Suspense fallback={<div className="h-36 animate-pulse rounded-md bg-muted" />}>
          <LoginForm />
        </Suspense>
      </div>
    </div>
  )
}
